import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Whatsapp } from "../../../../assets/media/icons";
import Container from "../../../../components/structure/Container";
import Flex from "../../../../components/structure/Flex";
import Button from "../../../../components/ui/Button";
import CollapsibleContent from "../../../../components/ui/CollapsibleContent";
import Icon from "../../../../components/ui/Icon";
import { Loading } from "../../../../components/ui/Loading";
import { AnimatedModal } from "../../../../components/ui/Modal";
import ZMap from "../../../../components/ui/ZMap";
import { CurrencyContext } from "../../../../contexts/currency";
import { Experience } from "../../../../definitions/experience";
import { Property } from "../../../../definitions/property";
import {
  useAuthenticatedApi,
  useUnauthenticatedApi,
} from "../../../../hooks/useApi";
import { humanizeNumbers } from "../../../../utils/format";
import ExperienceGrid from "../Explore/components/ExperienceGrid";
import PropertyGrid from "../Property/components/PropertyGrid";
import CancellationModalContent from "./components/CancellationModalContent";
import { trackMetaPixelEvent } from "../../../../utils/tracking";

interface Room {
  image?: string;
  name: string;
  units: number;
  price: number;
  offer_discount?: number;
}

interface BookingInfo {
  code: string;
  status: string;
  operator: {
    name: string;
    slug: string;
    images: { image: string }[];
    destination?: {
      slug: string;
      code: string;
      name: string;
      latitude?: number;
      longitude?: number;
    };
    address?: string;
    policy?: string;
    cancellation_policy?: string;
    latitude?: number;
    longitude?: number;
  };
  checkin: string;
  checkout: string;
  rooms: Room[];
  tax_amount: number;
  total_amount: number;
  paid_amount: number;
  directions?: string;
  destination?: {
    name: string;
    slug: string;
  };
  name: string;
}

const UserBookingInfo: React.FC = () => {
  const api = useAuthenticatedApi("STAY_MY_BOOKINGS_LIST");
  const bookingCancelInfoApi = useAuthenticatedApi("CRS_BOOKING_CANCEL_INFO");
  const bookingCancelApi = useAuthenticatedApi("CRS_BOOKING_CANCEL");
  const experienceByDestinationApi = useUnauthenticatedApi(
    "DISCOVER_DESTINATION"
  );
  const destinationApi = useUnauthenticatedApi("STAY_DESTINATIONS");

  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [refund, setRefund] = useState<any>(null);
  const [cancelSuccess, setCancelSuccess] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [moreProperties, setMoreProperties] = useState<Property[]>([]);
  const [experience, setExperience] = useState<Experience[]>([]);

  const { code } = useParams<{ code: string }>();
  const history = useHistory();
  const { formatCurrency } = useContext(CurrencyContext);

  // Fetch booking information
  useEffect(() => {
    if (api && code) {
      api
        .get(`/${code}/`)
        .then((res: any) => {
          setBookingInfo(res.data);
        })
        .catch((error) => {
          console.error("Error fetching booking info:", error);
        });
    }
  }, [api, code]);

  // Fetch additional properties based on destination
  useEffect(() => {
    if (destinationApi && bookingInfo) {
      const destinationSlug = bookingInfo.operator.destination?.slug;
      if (destinationSlug) {
        destinationApi
          .get(`/${destinationSlug}/`)
          .then((res) => {
            const properties: Property[] =
              res.data.destination.operators.filter(
                (property: Property) =>
                  property.slug !== bookingInfo.operator.slug &&
                  property.operating_model !== "T"
              );
            setMoreProperties(properties);
          })
          .catch((error) => {
            console.error("Error fetching more properties:", error);
          });
      }
    }
  }, [destinationApi, bookingInfo]);

  // Fetch experiences based on destination
  useEffect(() => {
    if (experienceByDestinationApi && bookingInfo) {
      const destinationCode = bookingInfo.operator.destination?.code;
      if (destinationCode) {
        experienceByDestinationApi
          .get(`/${destinationCode}/experiences/`)
          .then((res) => {
            setExperience(res.data.experiences);
          })
          .catch((error) => {
            console.error("Error fetching experiences:", error);
          });
      }
    }
  }, [experienceByDestinationApi, bookingInfo]);

  // Fetch cancellation info
  useEffect(() => {
    if (bookingCancelInfoApi && code) {
      bookingCancelInfoApi
        .get(`?code=${code}`)
        .then((res: any) => {
          if (res.status === 200 || res.status === 201) {
            setRefund(res.data);
          }
        })
        .catch((e) => {
          if (e.response?.data) {
            setErrorText(e.response.data);
          }
        });
    }
  }, [bookingCancelInfoApi, code]);

  const handleCancelBooking = () => {
    if (bookingCancelApi && code) {
      bookingCancelApi
        .get(`?code=${code}`)
        .then((res: any) => {
          if (res.status === 200 || res.status === 201) {
            setCancelSuccess(true);

            // Track cancellation event for stay booking
            if (bookingInfo) {
              trackMetaPixelEvent("BookingCancelled", {
                booking_id: code,
                property: bookingInfo.operator?.name || "",
                checkin_date: bookingInfo.checkin,
                checkout_date: bookingInfo.checkout,
                refund_amount: refund?.refund_amount || 0,
              });
            }
          }
        })
        .catch((error) => {
          console.error("Error cancelling booking:", error);
          setErrorText("Failed to cancel booking.");
        });
    }
  };

  const goBack = () => {
    history.push("/bookings/");
  };

  const totalNights = useMemo(() => {
    if (bookingInfo) {
      return moment(bookingInfo.checkout).diff(
        moment(bookingInfo.checkin),
        "days"
      );
    }
    return 0;
  }, [bookingInfo]);

  // Render loading state
  if (!bookingInfo) {
    return (
      <section className="w-full overflow-y-auto bg-gray-100 rounded-lg sm:p-4 sm:mt-8 sm:pb-4">
        <div className="flex flex-col items-center justify-center h-full">
          <div className="flex flex-col items-center">
            <Loading className="w-12" />
            <span className="mt-4 text-lg font-medium">Please Wait</span>
            <span className="mt-2 text-sm font-medium text-subtitle">
              Fetching Booking information
            </span>
          </div>
        </div>
      </section>
    );
  }

  const {
    code: bookingCode,
    status,
    operator,
    checkin,
    checkout,
    rooms,
    tax_amount,
    total_amount,
    paid_amount,
    directions,
    destination,
    name,
  } = bookingInfo;

  const pendingAmount = total_amount - paid_amount;

  return (
    <>
      <section className="w-full overflow-y-auto bg-gray-100 rounded-lg sm:p-4 sm:mt-8 sm:pb-4">
        {/* Booking Details */}
        <div>
          <Flex col>
            <Flex
              items="center"
              className="w-full px-1 -mb-2"
              justify="between"
            >
              <Flex items="center" className="px-2 mb-2 sm:px-0">
                <button className="mt-1 mr-4" onClick={goBack}>
                  <i className="text-3xl material-icons">arrow_back</i>
                </button>
                <Flex col>
                  <h1 className="text-xl font-medium">Booking</h1>
                  <span className="mb-2 text-sm font-medium text-text">
                    # {bookingCode}
                  </span>
                </Flex>
              </Flex>
            </Flex>

            <hr />

            <h1 className="px-4 mt-4 text-2xl font-medium">
              Stays at <span className="text-orange">{operator.name}</span>
            </h1>
          </Flex>

          <Container className="relative grid mt-4 bg-gray-100 rounded-lg sm:px-0 z-1 lg:grid-cols-2">
            {/* Booking Image and Status */}
            <div className="flex flex-col items-center w-full p-4 sm:transition sm:duration-500 sm:transform sm:cursor-pointer sm:hover:scale-105">
              <div className="relative flex-shrink-0 w-full h-56 overflow-hidden rounded-lg bg-subtitle">
                <img
                  src={operator.images[0].image}
                  alt={`${operator.name}`}
                  className="object-cover object-center w-full h-56"
                />
                <div className="absolute top-0 right-0 p-1 m-2 text-xs font-medium text-primary bg-white rounded-md">
                  <span className="p-2 text-xs font-extrabold text-gray-800 uppercase rounded-full">
                    {status}
                  </span>
                </div>
              </div>
            </div>

            {/* Booking Info */}
            <Flex col className="w-full p-4">
              <Flex className="w-full">
                {/* Check-in */}
                <Flex col className="w-1/2">
                  <span className="text-xs font-medium tracking-wide text-gray-600 uppercase sm:mb-2">
                    Check in
                  </span>
                  <span className="text-xs font-medium tracking-wide uppercase sm:mb-2">
                    {moment(checkin).format("D MMM' YY")}
                  </span>
                </Flex>

                {/* Check-out */}
                <Flex col items="center" className="w-1/2">
                  <span className="text-xs font-medium tracking-wide text-gray-600 uppercase sm:mb-2">
                    Check out
                  </span>
                  <span className="text-xs font-medium tracking-wide uppercase sm:mb-2">
                    {moment(checkout).format("D MMM' YY")}
                  </span>
                </Flex>
              </Flex>

              {/* Contact Buttons */}
              <Flex col className="w-full">
                <Flex className="gap-4">
                  <a
                    href={`https://api.whatsapp.com/send?phone=919289229822&text=Hi%20Zobu,%20I%20want%20to%20talk%20about%20${operator.name}!`}
                    className="flex items-center justify-center w-full px-4 py-2 mt-2 bg-white border rounded-lg border-accent text-orange"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Whatsapp className="w-4 h-4 mr-2 fill-current" />
                    Whatsapp us
                  </a>
                  <a
                    href={`https://api.whatsapp.com/send?phone=919289229822&text=Hi%20Zobu,%20I%20want%20to%20talk%20about%20Zostel`}
                    className="flex items-center justify-center w-full px-4 py-2 mt-2 bg-white border rounded-lg border-accent text-orange"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon className="mr-2">call</Icon> Call
                  </a>
                </Flex>
              </Flex>
            </Flex>
          </Container>
        </div>

        {/* Booking Summaries */}
        <Flex col className="px-4 pb-4 sm:pl-1 sm:pr-2">
          {/* Rooms Summary */}
          <div className="flex flex-col w-full mt-8">
            <h2 className="text-2xl font-medium">Rooms Summary</h2>
            <div className="mt-4 flow-root">
              <ul className="divide-y divide-gray-200">
                {rooms.map((room, index) => (
                  <li key={index} className="flex py-4">
                    {room.image && (
                      <div className="flex-shrink-0 w-20 h-20 overflow-hidden border border-gray-200 rounded-md">
                        <img
                          src={room.image}
                          alt={room.name}
                          className="object-cover object-center w-full h-full"
                        />
                      </div>
                    )}
                    <div className="flex flex-col flex-1 ml-4">
                      <div className="flex justify-between text-sm font-medium text-gray-900">
                        <span>
                          {room.name} x {room.units}
                        </span>
                        <p className="ml-4">
                          {formatCurrency(
                            room.offer_discount != null &&
                              room.offer_discount < 0
                              ? room.price - room.offer_discount
                              : room.price
                          )}
                        </p>
                      </div>
                      <p className="mt-1 text-sm text-dark">
                        {formatCurrency(
                          room.offer_discount != null && room.offer_discount < 0
                            ? (room.price - room.offer_discount) / totalNights
                            : room.price / totalNights
                        )}{" "}
                        *{" "}
                        {totalNights > 1
                          ? `${totalNights} nights`
                          : `${totalNights} night`}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Price Summary */}
          <div className="flex flex-col w-full">
            <h2 className="text-2xl font-medium">Price Summary</h2>
            <div className="px-4 my-4 bg-gray-100 rounded-lg">
              <ul className="divide-y divide-gray-200">
                <li className="flex py-4">
                  <div className="flex justify-between w-full text-base font-medium text-gray-900">
                    <span>Tax</span>
                    <span>₹{humanizeNumbers(tax_amount)}</span>
                  </div>
                </li>
                <li className="flex py-4">
                  <div className="flex justify-between w-full text-base font-medium text-gray-900">
                    <span>Total (tax incl.)</span>
                    <span>₹{humanizeNumbers(total_amount)}</span>
                  </div>
                </li>
                <li className="flex py-4">
                  <div className="flex justify-between w-full text-base font-medium text-gray-900">
                    <span>Pending Amount</span>
                    <span className="text-lg font-medium text-text">
                      ₹{humanizeNumbers(pendingAmount)}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* How To Reach */}
          <div className="flex flex-col w-full mt-8">
            <h2 className="text-2xl font-medium">How To Reach</h2>

            {operator.latitude && operator.longitude && (
              <Container className="px-4 my-4" id="map">
                <Flex className="flex-col sm:flex-row">
                  {/* Address */}
                  <div className="w-full sm:w-1/2 sm:mr-4">
                    {operator.address && operator.address !== "<p><br></p>" && (
                      <div className="flex flex-col p-2 space-y-1 border border-gray-200 rounded-lg">
                        <span className="font-medium">Address</span>
                        <div
                          className="text-xs"
                          dangerouslySetInnerHTML={{ __html: operator.address }}
                        />
                        <a
                          className="flex items-center px-4 py-2 mt-4 text-xs text-orange"
                          href={`https://www.google.com/maps/dir/?api=1&destination=${operator.latitude},${operator.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icon className="mr-2 text-xs">map</Icon>
                          Tap To Open Maps
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Map */}
                  <ZMap
                    centerLatitude={operator.latitude}
                    centerLongitude={operator.longitude}
                    className="z-0 w-full h-40 mt-4 rounded-lg sm:mt-0"
                    zoom={12}
                  />
                </Flex>
              </Container>
            )}

            {/* Directions */}
            <div className="grid w-full grid-cols-1 gap-4 mt-6 html-content">
              {directions && directions !== "<p><br></p>" && (
                <CollapsibleContent
                  expandOnHeaderClick
                  title="Directions"
                  html={directions}
                />
              )}
            </div>
          </div>

          {/* Policies */}
          {(operator.policy || operator.cancellation_policy) && (
            <div className="flex flex-col w-full mt-8">
              <div className="flex flex-col">
                <h2 className="text-2xl font-medium">Our Policies</h2>
                <span className="font-medium text-orange">
                  Please read them once
                </span>
              </div>
              <div className="grid w-full grid-cols-1 gap-4 mt-6 html-content">
                {operator.policy && (
                  <CollapsibleContent
                    expandOnHeaderClick
                    title="General Policy"
                    html={operator.policy}
                  />
                )}
                {operator.cancellation_policy && (
                  <CollapsibleContent
                    expandOnHeaderClick
                    title="Cancellation Policy"
                    html={operator.cancellation_policy}
                  />
                )}
              </div>
            </div>
          )}
        </Flex>

        {/* Cancel Booking Button */}
        <Flex className="justify-end p-4">
          <Button
            className="mt-8"
            type="primary"
            disabled={
              status === "cancelled" || moment(checkin).isSameOrBefore(moment())
            }
            onClick={() => setModalVisible(true)}
          >
            Cancel
          </Button>
        </Flex>

        {/* More Properties */}
        {moreProperties.length > 0 && (
          <Container className="p-4">
            <h2 className="text-lg font-medium sm:text-xl">
              Other <span className="text-orange"> Stays</span> near{" "}
              {destination?.name}
            </h2>
            <PropertyGrid
              data={moreProperties}
              slidesPerViewNum={3}
              slug={destination?.slug || ""}
            />
          </Container>
        )}

        {/* Experiences */}
        {experience.length > 0 && (
          <Container className="p-4">
            <h2 className="text-xl font-medium sm:text-3xl">
              Explore <span className="text-orange"> Trips</span> Around {name}
            </h2>
            <ExperienceGrid data={experience} slidesPerViewNum={3} />
          </Container>
        )}
      </section>

      {/* Cancellation Modal */}
      <AnimatedModal
        visible={modalVisible}
        className="relative p-4 m-2 overflow-y-auto bg-white border rounded-lg w-144 sm:m-16 h-108"
      >
        <CancellationModalContent
          cancelSuccess={cancelSuccess}
          errorText={errorText}
          refund={refund}
          closeModal={() => setModalVisible(false)}
          handleCancelBooking={handleCancelBooking}
        />
      </AnimatedModal>
    </>
  );
};

export default UserBookingInfo;
